/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";

import {
  useLazyGetLoanApplicationByIdSecondGuaranteeQuery,
  useLazyGetPersonByIdGuaranteeQuery,
  usePatchUsuarioStatusSecondGuaranteeMutation,
  usePostDocumentGenarateSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import {
  setStatusDigitalFileSecondGuarantee,
  setDocumentValueSecondGuarantee,
  setCurretStepSecondGuarantee,
  setConyugeSecondGuarantee,
  setDocumentValueConyugeSecondGuarantee,
} from "@store/slices/segundoAvalSlice";
import SegundoAvalDocuments from "@components/TabBofu/SegundoAvalDocuments";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import { BOFU_TABS_CONFIG, MATRIMONIAL_REGIMEN_CODES, SECOND_GUARANTEE_STATUS_FINAL } from "@helpers/constantes";
import CustomButtonExpedienteAvalAdicionalV2 from "@components/CustomButtonExpedienteAvalAdicionalV2";
import { setLoanApplication } from "@store/slices/registerSlice";
import { useLazyGetMatrimonialRegimesSecondGuaranteeQuery } from "@api/second_guarantee_api/catalogsSecondGuarantee";

const ExpedienteSegundoAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const shareholder = useSelector((state: RootState) => state.secondGuarante.guarantee);
  const conyugue = useSelector((state: RootState) => state.secondGuarante.conyugeSecondGuarantee);

  const [patchStatus] = usePatchUsuarioStatusSecondGuaranteeMutation();
  const [getPerson] = useLazyGetPersonByIdGuaranteeQuery();
  const [createDocument] = usePostDocumentGenarateSecondGuaranteeMutation();
  const [getLoadApplication] = useLazyGetLoanApplicationByIdSecondGuaranteeQuery();
  const [getMatrimonialRegimen] = useLazyGetMatrimonialRegimesSecondGuaranteeQuery();

  const [isLoading, setIsLoading] = useState(false);
  const [regimenMatrimonialAval, setRegimenMatrimonialAval] = useState<string>("");
  const [isMarriage, setIsMarriage] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!shareholder.id) return;
      
      try {
        setIsLoading(true);
        const responsePerson = await getPerson(String(shareholder.id)).unwrap();

        const responseApplication = await getLoadApplication(responsePerson.loan_application_id).unwrap();
        const res_matrimonial_regimen = await getMatrimonialRegimen().unwrap();

        const regimenFound = res_matrimonial_regimen.find((regimen) => regimen.id === responsePerson.matrimonial_regimes_id);
        
        if (regimenFound) {
          setRegimenMatrimonialAval(regimenFound.code);
          if (regimenFound.code === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code) {
            setIsMarriage(true);
            anexoConyuge();
          }
        }

        if (responsePerson.spouse_id) {
          const responseConyugue = await getPerson(String(responsePerson.spouse_id)).unwrap();
          dispatch(setConyugeSecondGuarantee(responseConyugue));
        }

        dispatch(setLoanApplication(responseApplication));
        dispatch(
          setStatusDigitalFileSecondGuarantee({
            bank_references_completed: responsePerson?.bank_references_completed,
            identity_validation_completed: responsePerson?.identity_validation_completed,
            information_person_completed: responsePerson?.information_person_completed,
            personal_information_status: responsePerson?.personal_information_status,
            bank_references_status: responsePerson?.bank_references_status,
          }),
        );

        await generateSolicitudCredito();
      } catch (error) {
        const api_error = error as ApiError;
        console.error("Error al obtener los datos iniciales", error);
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [shareholder.id]);

  const generateSolicitudCredito = async () => {
    try {
      const all_forms_completed =
        shareholder?.digital_file?.personal_information_status === "COMPLETO" &&
        shareholder?.digital_file?.identity_validation_completed &&
        shareholder?.digital_file?.bank_references_status === "COMPLETO";

      if (!all_forms_completed) {
        return;
      }

      const docAnexo = shareholder?.doc_anexo_1;
      const docAnexoValido = docAnexo && typeof docAnexo === "object" && !!docAnexo.url && docAnexo.status === true;

      if (docAnexoValido) {
        return;
      }

      const payload_document = {
        loan_application_id: String(shareholder.loan_application_id),
        document_type: "anexo_1",
        person_id: String(shareholder.id),
      };

      await createDocument(payload_document).unwrap();
    } catch (error) {
      console.log("Error al generar el documento de solicitud de crédito", error);
    }
  };

  const validConyugeSection = () => {
    const all_forms_completed =
      conyugue?.bank_references_status === "COMPLETO" && conyugue?.identity_validation_completed;

    const uploadDocuments = shareholder.documents_conyuge || [];
    const allHaveFileValue = uploadDocuments.every((doc) => doc.file_value && Object.keys(doc.file_value).length > 0);

    const docAnexo = shareholder?.doc_anexo_1_conyuge;
    const docAnexoValido = docAnexo && typeof docAnexo === "object" && !!docAnexo.url && docAnexo.status === true;

    const isReadyToProceed = all_forms_completed && allHaveFileValue && docAnexoValido;

    return isReadyToProceed;
  };

  const anexoConyuge = async () => {
    try {
      if (!isMarriage) return;

      const all_forms_completed =
        conyugue?.bank_references_status === "COMPLETO" && conyugue?.identity_validation_completed;

      if (!all_forms_completed) {
        return;
      }

      const docAnexo = shareholder?.doc_anexo_1_conyuge;
      const docAnexoValido = docAnexo && typeof docAnexo === "object" && !!docAnexo.url && docAnexo.status === true;

      if (docAnexoValido) {
        return;
      }

      const payload_document = {
        loan_application_id: String(shareholder.loan_application_id),
        document_type: "anexo_1",
        person_id: String(conyugue.id),
      };

      await createDocument(payload_document).unwrap();
    } catch (error) {
      console.log("Error al generar el documento de solicitud de crédito", error);
    }
  };

  const finalStatus = async () => {
    try {
      const all_forms_completed =
        shareholder?.digital_file?.personal_information_status === "COMPLETO" &&
        shareholder?.digital_file?.identity_validation_completed &&
        shareholder?.digital_file?.bank_references_status === "COMPLETO";

      const uploadDocuments = shareholder.documents || [];

      const allHaveFileValue = uploadDocuments.every((doc) => doc.file_value && Object.keys(doc.file_value).length > 0);

      const docAnexo = shareholder?.doc_anexo_1;
      const docAnexoValido = docAnexo && typeof docAnexo === "object" && !!docAnexo.url && docAnexo.status === true;

      const isReadyToProceed = all_forms_completed && allHaveFileValue && docAnexoValido;

      const conyugeReady = validConyugeSection();

      if (isReadyToProceed && conyugeReady) {
        await patchStatus({
          usuario: String(shareholder.user_id),
          body: {
            status_id: SECOND_GUARANTEE_STATUS_FINAL.status_id,
          },
        }).unwrap();

        dispatch(
          setCurretStepSecondGuarantee({
            code: String(SECOND_GUARANTEE_STATUS_FINAL?.code),
            url: String(SECOND_GUARANTEE_STATUS_FINAL?.url),
            sequence: Number(SECOND_GUARANTEE_STATUS_FINAL?.sequence),
            status_id: String(SECOND_GUARANTEE_STATUS_FINAL?.status_id),
          }),
        );

        navigate(SECOND_GUARANTEE_STATUS_FINAL.url);
      }
    } catch (error) {
      console.error("Error al actualizar el estatus final:", error);
    }
  };

  useEffect(() => {
    finalStatus();
  }, [shareholder]);


  return (
    <Grid container>
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        {!isLoading && <SegundoAvalDocuments isMarriageAval={isMarriage} />}
        <Box textAlign={"center"} mx={4}>
          <Typography
            mt={4}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            sx={{ color: theme?.palette?.primary?.dark }}
          >
            Expediente digital
          </Typography>

          <Typography
            mt={2}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={600}
            color={theme?.palette?.primary?.dark}
          >
            Ahora es momento de llenar los formatos relacionados al aval
          </Typography>

          <Typography
            mt={2}
            variant={isMobileOrTablet ? "body2" : "body1"}
            sx={{ color: theme?.palette?.primary?.main, fontSize: "16px", fontWeight: 300 }}
          >
            {shareholder.personalInfo.name} {shareholder.personalInfo.first_lastname}{" "}
            {shareholder.personalInfo.second_lastname}
          </Typography>

          {isLoading || shareholder.documents_loading ? (
            <>
              <Box mt={4}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color={theme?.palette?.primary?.dark}>
                    Información del aval
                  </Typography>

                  <CustomButtonExpedienteAvalAdicionalV2
                    onClick={() => navigate("/avales/informacion_aval_1")}
                    formStatus={shareholder?.digital_file.personal_information_status}
                  />
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color={theme?.palette?.primary?.dark}>
                    Validación de identidad
                  </Typography>

                  <CustomButtonExpedienteAvalAdicionalV2
                    onClick={() => navigate("/avales/validacion_identidad_aval")}
                    formStatus={shareholder?.digital_file.identity_validation_completed}
                  />
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" sx={{ flex: 1, textAlign: "left" }} color={theme?.palette?.primary?.dark}>
                    Información bancaria
                  </Typography>

                  <CustomButtonExpedienteAvalAdicionalV2
                    onClick={() => navigate("/avales/informacion_bancaria_aval")}
                    formStatus={shareholder?.digital_file.bank_references_status}
                  />
                </Box>

                <Typography
                  mt={2}
                  variant={isMobileOrTablet ? "body2" : "body1"}
                  fontWeight={600}
                  sx={{ color: theme?.palette?.primary?.dark }}
                >
                  Ahora carga los documentos relacionados al aval
                </Typography>

                <DocumentsTabBofu
                  tabCode="TBAVL"
                  uploadService="second_guarantee"
                  documents={shareholder.documents}
                  onChange={({ document, newValue }) => {
                    dispatch(
                      setDocumentValueSecondGuarantee({
                        document,
                        newValue,
                      }),
                    );
                  }}
                />

                {conyugue && (
                  <Box mt={3}>
                    <Box>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={700}
                        color={theme?.palette?.primary?.dark}
                      >
                        Información del Conyuge del Aval
                      </Typography>

                      <Box
                        mt={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        color={theme?.palette?.primary?.dark}
                      >
                        <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ flex: 1, textAlign: "left" }}>
                          Datos generales
                        </Typography>

                        <CustomButtonExpedienteAvalAdicionalV2
                          formStatus={conyugue?.personal_information_status}
                          onClick={() => navigate("/avales/informacion_conyuge_segundo_aval")}
                        />
                      </Box>

                      {regimenMatrimonialAval === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code && (
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                            color={theme?.palette?.primary?.dark}
                          >
                            Validación de identidad
                          </Typography>

                          <CustomButtonExpedienteAvalAdicionalV2
                            formStatus={conyugue?.identity_validation_completed}
                            onClick={() => navigate("/avales/validacion_identidad_conyuge")}
                          />
                        </Box>
                      )}
                    </Box>

                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={700}
                      color={theme?.palette?.primary?.dark}
                      mt={5}
                      mb={4}
                    >
                      Ahora carga los documentos relacionados al conyuge
                    </Typography>

                    <DocumentsTabBofu
                      inputPrefix="conyuge_aval"
                      tabCode={BOFU_TABS_CONFIG.tab_aval.code}
                      uploadService="second_guarantee"
                      documents={shareholder.documents_conyuge || []}
                      onChange={({ document, newValue }) => {
                        dispatch(
                          setDocumentValueConyugeSecondGuarantee({
                            document,
                            newValue,
                          }),
                        );
                      }}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid
          item
          xs={12}
          md={4}
          style={{
            height: "90vh",
          }}
        >
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ExpedienteSegundoAval;
